import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import { initialLocalAutomationPortionsModalState, LocalAutomationPortionsComboParsing, LocalAutomationPortionsModalComboElement, LocalAutomationPortionsModalState } from '@/modules/configurationModule/detail/automation/portions/components/portions-modal/configuration-automation-portions-modal.model'
import { ConfigurationDefaultPortionCreateRequest, ConfigurationDefaultPortionPatchInBatchRequest, ConfigurationDefaultPortionResponse } from '@/modules/configurationModule/detail/automation/portions/services/configuration-portions.dto'
import { commonPortionTypeService } from '@/api/services/common/portion-type/common-portion-type.service'
import { CommonPortionTypeResponse } from '@/api/services/common/portion-type/common-portion-type.dto'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { BModal } from 'bootstrap-vue'

@Component
export default class AutomationPortionsModal extends Vue {
    @Ref() readonly localAutomationPortionsModal!: BModal

    @Prop(String) readonly actionTitle!: string
    @Prop(Number) readonly laboratoryId!: number

    @Emit()
    update(defaultPortionTypePatch: ConfigurationDefaultPortionPatchInBatchRequest[]): ConfigurationDefaultPortionPatchInBatchRequest[] {
        return defaultPortionTypePatch
    }

    @Emit()
    create(defaultPortionTypeCreate: ConfigurationDefaultPortionCreateRequest): ConfigurationDefaultPortionCreateRequest {
        return defaultPortionTypeCreate
    }

    state: LocalAutomationPortionsModalState
    constructor() {
        super()
        this.state = initialLocalAutomationPortionsModalState()
    }

    get canSelectProcedure(): boolean {
        return !!this.state.topographicCodes.selected?.id
    }

    get canUpdateCombo(): boolean {
        return !this.state?.id
    }

    get canUpdate(): boolean {
        const hasError: boolean = !!this.errors.count()
        const anyFieldIsDirty: boolean = this.$_someFieldIsDirty(this.fields)
        const comboSelectionValid: boolean = !!this.state.sampleTypes.selected?.id && !!this.state.portionTypes.selected?.id
        return !hasError && anyFieldIsDirty && comboSelectionValid
    }

    async show(selectedRow: Nullable<ConfigurationDefaultPortionResponse>): Promise<void> {
        this.state = initialLocalAutomationPortionsModalState()
        await this._fillAllCombos()
        await this.$validator.reset()
        if (selectedRow) {
            this.updateRow(selectedRow)
            await this._setSelectionCombo(selectedRow)
        }
        this._showModal()
    }

    hide(): void { this.localAutomationPortionsModal.hide() }

    updateRow(selectedRow: Nullable<ConfigurationDefaultPortionResponse>): void {
        this._setSelectedRow(selectedRow)
    }

    spreadEvent(): void {
        if (this.state?.id) {
            const patchRow: ConfigurationDefaultPortionPatchInBatchRequest = {
                id: this.state.id,
                active: this.state.active,
                numberOfPortionsToGenerate: this.state.numberOfPortionsToGenerate
            }
            this.update([patchRow])
        } else {
            const postRow: ConfigurationDefaultPortionCreateRequest = {
                sampleTypeId: this.state.sampleTypes.selected?.id!,
                topographicCodeId: this.state.topographicCodes.selected?.id!,
                procedureId: this.state.procedures.selected?.id,
                active: this.state.active,
                numberOfPortionsToGenerate: this.state.numberOfPortionsToGenerate,
                portionTypeId: this.state.portionTypes.selected?.id!
            }
            this.create(postRow)
        }
    }

    private _setSelectedRow(selectedRow: Nullable<ConfigurationDefaultPortionResponse>): void {
        this.state = {
            ...this.state,
            id: selectedRow?.id,
            active: selectedRow?.active!,
            numberOfPortionsToGenerate: selectedRow?.numberOfPortionsToGenerate!
        }
    }

    private async _getCommonSampleTypes(): Promise<void> {
        const response = await this.$_toCallSecureLoading(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (response) {
            this.state.sampleTypes.list = this._parseCombo<CommonSampleTypeResponse>(response)
        }
    }

    private async _getCommonTopographicCodes(): Promise<void> {
        const response = await this.$_toCallSecureLoading(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (response) {
            this.state.topographicCodes.list = this._parseCombo<CommonTopographicCodeResponse>(response)
        }
    }

    private async _getCommonProcedures(): Promise<void> {
        const response = await this.$_toCallSecureLoading(commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (response) {
            this.state.procedures.list = this._parseCombo<CommonProcedureResponse>(response)
        }
    }

    private async _getCommonPortionTypes(): Promise<void> {
        const response = await this.$_toCallSecureLoading(commonPortionTypeService.getPortionTypes())
        if (response) {
            this.state.portionTypes.list = response.map((item: CommonPortionTypeResponse) => {
                return {
                    id: item?.potId,
                    text: item?.potName
                }
            })
        }
    }

    private async _setSelectionCombo(selectedRow: Nullable<ConfigurationDefaultPortionResponse>): Promise<void> {
        this.state.sampleTypes.selected = this.state.sampleTypes.list.find((itemList: LocalAutomationPortionsModalComboElement) => itemList.id === selectedRow?.sampleType?.id)
        this.state.topographicCodes.selected = this.state.topographicCodes.list.find((itemList: LocalAutomationPortionsModalComboElement) => itemList.id === selectedRow?.topographicCode?.id)
        this.state.procedures.selected = this.state.procedures.list.find((itemList: LocalAutomationPortionsModalComboElement) => itemList.id === selectedRow?.procedure?.id)
        this.state.portionTypes.selected = this.state.portionTypes.list.find((itemList: LocalAutomationPortionsModalComboElement) => itemList.id === selectedRow?.portionType?.potId)
    }

    private _showModal(): void {
        this.localAutomationPortionsModal.show()
    }

    private _parseCombo<T extends LocalAutomationPortionsComboParsing>(data: T[]): LocalAutomationPortionsModalComboElement[] {
        return data?.map((itemData: T) => {
            const { id, description: text } = itemData
            return { id, text }
        })
    }

    private async _fillAllCombos(): Promise<void> {
        this.$_updateLoading(true)
        await Promise.all([
            this._getCommonSampleTypes(),
            this._getCommonTopographicCodes(),
            this._getCommonProcedures(),
            this._getCommonPortionTypes()
        ])
        this.$_updateLoading(false)
    }
}
